import {
	Divider,
	Headline,
	Layout,
	SelectField,
	Text,
	TextLink,
	TextList,
	TextListItem,
} from '@oneaudi/unified-web-alpha-components';
import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { DialogItemActionButton } from '~/app/pages/configurator/components/more-information-dialog/components/dialog-item-action-button';
import { Dialog } from '~/components/dialog';
import { useFootnotes } from '~/components/footnotes';
import { MONTHLY_RATE_DIALOG_REMARK } from '~/components/footnotes/constants';
import { Prices } from '~/components/prices';
import { RenderHtml } from '~/components/text';
import { SelectionCardItem } from '~/graphql/types';
import { state } from '~/storage';
import { StyledColumnContainer } from '../styles/comparison-dialog-styles';
import { htmlToText } from 'html-to-text';

export function TechPackagesCompare({ items }: { items: SelectionCardItem[] }) {
	const { i18n } = useI18n();
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Dialog
			triggerElement={
				<TextLink margin={[0, 0, '16px', 0]} variant={'standalone'} size={'sm'} onClick={() => setIsOpen(true)}>
					{i18n('ngc.dialogs.comparison.trigger')}
				</TextLink>
			}
			onCloseButtonClick={() => setIsOpen(false)}
			open={isOpen}>
			<TechPackagesCompareContent items={items} setIsOpen={setIsOpen} />
		</Dialog>
	);
}

function TechPackagesCompareContent({
	items,
	setIsOpen,
}: {
	setIsOpen: (isOpen: boolean) => void;
	items: SelectionCardItem[];
}) {
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { addFootnote } = useFootnotes();
	const disclaimer = useAtomValue(state.financingDisclaimerText);
	const [firstTechPackage, setFirstTechPackage] = useState(items[0]);
	const [secondTechPackage, setSecondTechPackage] = useState(items[1]);

	const { getReference } = useFootnotes();
	const vatConfig = useVATConfig();

	const selectedItems = [firstTechPackage, secondTechPackage];

	if (config.enableFinancingServices && !config.hideMonthlyRates && config.showFinancingDisclaimerInDialogs) {
		addFootnote(MONTHLY_RATE_DIALOG_REMARK, disclaimer);
	}
	const onAddToConfiguration = useCallback(
		(item: SelectionCardItem) => {
			setIsOpen(false);
			item.onSelect();
		},
		[setIsOpen],
	);

	return (
		<>
			<Headline tag="h2" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
				{i18n('ngc.dialogs.comparison.title')}
			</Headline>
			<StyledColumnContainer>
				{selectedItems.map((selectedItem, index) => (
					<div key={index}>
						<SelectField
							id={'select-field'}
							label={i18n('ngc.dialogs.comparison.select')}
							value={selectedItem.name || ''}
							onChange={(e) => {
								const foundItem = items.find((item) => item.name === e.target.value);
								(index === 0 ? setFirstTechPackage : setSecondTechPackage)(foundItem ?? selectedItem);
							}}
							margin={[0, 0, '24px', 0]}>
							{items.map((item) => (
								<option key={item.key} value={item.name || ''}>
									{item.name}
								</option>
							))}
						</SelectField>
						<img
							className="border-radius-md"
							src={selectedItem.imgUrl}
							alt={selectedItem.name ? htmlToText(selectedItem.name) : ''}
						/>
					</div>
				))}
			</StyledColumnContainer>
			<StyledColumnContainer>
				{selectedItems.map((selectedItem, index) => (
					<div key={index}>
						<Headline
							tag="h2"
							variant={{ type: 'headline', order: '3', weight: 'bold' }}
							margin={['8px', 0, '-8px', 0]}>
							{selectedItem.name}
						</Headline>
					</div>
				))}
			</StyledColumnContainer>

			<StyledColumnContainer>
				{selectedItems.map((selectedItem, index) => (
					<div key={index}>
						<Prices
							totalPrice={selectedItem.price?.totalPrice}
							pr7={selectedItem.key}
							monthlyRateDialog={true}
							monthlyRateDialogReference={getReference(MONTHLY_RATE_DIALOG_REMARK)}
							vatConfig={vatConfig.feature}
							displayMode="cols"
							size="1"
						/>
						<Layout margin={['16px', 0, '8px', 0]}>
							<DialogItemActionButton
								isFullWidth={true}
								status={selectedItem.status!}
								onAddToConfiguration={() => onAddToConfiguration(selectedItem)}
							/>
						</Layout>
					</div>
				))}
			</StyledColumnContainer>
			<StyledColumnContainer>
				{selectedItems.map((selectedItem, index) => (
					<div key={index}>
						<Text key={selectedItem.key} variant={{ type: 'copy', order: '2', style: 'normal' }}>
							<RenderHtml html={selectedItem.description} />
						</Text>
					</div>
				))}
			</StyledColumnContainer>
			<StyledColumnContainer>
				{selectedItems.map((selectedItem, index) => (
					<div key={index}>
						<Divider margin={['-20px', 0, '36px', 0]} />
						<Text className="features-title" variant={{ type: 'copy', order: '1', style: 'bold' }}>
							{i18n('ngc.dialogs.features')}
						</Text>
						<TextList margin={[0, 0, '4px', 0]} variant={'bullet'}>
							{selectedItem.content?.map((feature, featureIndex) => (
								<TextListItem key={featureIndex}>
									<Text variant={{ type: 'copy', order: '2', style: 'normal' }}>{feature.name}</Text>
								</TextListItem>
							))}
						</TextList>
					</div>
				))}
			</StyledColumnContainer>
		</>
	);
}
